const useChatLocalization = ({
  translations,
  language,
  chatFlowType,
  effectiveAgentId,
  getTranslatedOptionsHelper,
}) => {
  const normalizedLanguage = String(language || 'EN')
    .trim()
    .toUpperCase()
  const selectedLanguage = ['EN', 'FR', 'NL', 'SW'].includes(normalizedLanguage)
    ? normalizedLanguage
    : 'EN'

  const isExistingUser = chatFlowType
    ? chatFlowType === 'existing'
    : !!effectiveAgentId
  const findLabeledEntry = (value, stepId) => {
    if (Array.isArray(value)) {
      for (const entry of value) {
        const match = findLabeledEntry(entry, stepId)
        if (match) return match
      }
      return null
    }

    if (!value || typeof value !== 'object') return null
    if (value.label === stepId) return value

    for (const entry of Object.values(value)) {
      const match = findLabeledEntry(entry, stepId)
      if (match) return match
    }

    return null
  }

  const findFlowEntry = (stepId) => {
    const primaryFlowSection = isExistingUser
      ? 'existingUserFlow'
      : 'newUserFlow'

    const flowEntries = [
      translations?.[primaryFlowSection]?.questions,
      translations?.subscription?.[primaryFlowSection]?.questions,
      translations?.[primaryFlowSection],
      translations?.subscription?.[primaryFlowSection],

      translations?.subscription?.questions,

      ...(isExistingUser
        ? [
            translations?.newUserFlow?.questions,
            translations?.subscription?.newUserFlow?.questions,
            translations?.newUserFlow,
            translations?.subscription?.newUserFlow,
          ]
        : []),
    ]

    for (const entries of flowEntries) {
      const entry = findLabeledEntry(entries, stepId)

      if (entry) return entry
    }

    return null
  }

  const findClarificationEntry = (stepId) => {
    const flowSection = isExistingUser ? 'existingUserFlow' : 'newUserFlow'
    const clarificationSets = [
      translations?.[flowSection]?.clarifications,
      translations?.subscription?.[flowSection]?.clarifications,
      translations?.subscription?.clarifications,
    ]

    for (const clarificationSet of clarificationSets) {
      const entry = clarificationSet?.[stepId]
      if (entry) return entry
    }

    return null
  }

  const getLocalizedText = (stepId, lang = selectedLanguage) => {
    const requestedLanguage = String(lang || 'EN')
      .trim()
      .toUpperCase()
    const entry = findFlowEntry(stepId) || findClarificationEntry(stepId)

    return (
      entry?.question?.[requestedLanguage] ||
      entry?.question?.EN ||
      entry?.[requestedLanguage] ||
      entry?.EN ||
      null
    )
  }

  const getLocalizedAnswers = (stepId, lang = selectedLanguage) => {
    const requestedLanguage = String(lang || 'EN')
      .trim()
      .toUpperCase()
    const answers = findFlowEntry(stepId)?.answers
    return answers?.[requestedLanguage] || answers?.EN || null
  }

  const getTranslatedOptions = (step) => {
    if (step?.id === 'last_visit_summary') {
      const flowSection = isExistingUser ? 'existingUserFlow' : 'newUserFlow'
      const menuOptions =
        translations?.[flowSection]?.menuOptions ||
        translations?.subscription?.[flowSection]?.menuOptions ||
        []

      return menuOptions.map((option) => ({
        value: option.label,
        label:
          option.title?.[selectedLanguage] || option.title?.EN || option.label,
      }))
    }

    if (step?.id === 'profile_confirm_question') {
      return ['Yes', 'No'].map((answer) => {
        const label =
          answer === 'Yes' ? 'profile_confirm_yes' : 'profile_confirm_no'
        const entry = findFlowEntry(label)

        return {
          value: answer,
          label: entry?.[selectedLanguage] || entry?.EN || answer,
        }
      })
    }

    if (step?.id === 'analysis_recipient') {
      const recipientLabels = {
        EN: ['Adult', 'Child'],
        FR: ['Un adulte', 'Un enfant'],
        NL: ['Een volwassene', 'Een kind'],
        SW: ['Mtu mzima', 'Mtoto'],
      }
      const labels = recipientLabels[selectedLanguage] || recipientLabels.EN

      return ['Adult', 'Child'].map((value, index) => ({
        value,
        label: labels[index],
      }))
    }

    return getTranslatedOptionsHelper({
      step,
      localizedAnswers: getLocalizedAnswers(step.id),
      englishAnswers: getLocalizedAnswers(step.id, 'EN'),
    })
  }

  const getLocalizedWelcomeMessage = ({ isExistingUser, agentFullName }) => {
    const section = isExistingUser ? 'existingUserFlow' : 'newUserFlow'
    const template =
      translations?.[section]?.welcomeMessage?.[selectedLanguage] ||
      translations?.subscription?.[section]?.welcomeMessage?.[selectedLanguage]

    if (!template) return null

    const firstName = agentFullName?.split(' ')[0] || 'there'
    return template.replace('{firstName}', firstName)
  }

  return {
    findFlowEntry,
    getLocalizedText,
    getLocalizedAnswers,
    getTranslatedOptions,
    getLocalizedWelcomeMessage,
  }
}

export default useChatLocalization
